import { useTranslation } from 'react-i18next';
import { NavLink, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ReportingPaths } from '../../pods/reporting/reporting.router';
import { PageBreadcrumbs, PageNavigation } from '@client/toolkit';
import { FaXmark, FaChevronDown, FaArrowRight } from 'react-icons/fa6';
import classNames from 'classnames';
import { LockIcon } from '../icons/lock-icon';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useUserPackages } from '../../hooks/useUserPackages';

/** A single report link. `view` distinguishes the two portfolio-diversification entries. */
type ReportItem = {
  name: string;
  path: string;
  hasAccess: boolean;
  isNew?: boolean;
  view?: 'geography' | 'segment';
};

type ReportGroup = { name: string; items: ReportItem[] };

export function ReportsMenu({ isOpen, setIsOpen }: { isOpen: boolean; setIsOpen: (isOpen: boolean) => void }) {
  const { t } = useTranslation('global');
  const { pathname, search } = useLocation();
  const { getAvailablePackages } = useUserPackages();

  const currentView = useMemo(() => new URLSearchParams(search).get('view'), [search]);

  const groups = useMemo<ReportGroup[]>(() => {
    const { management, economy, eos, portfolio } = getAvailablePackages();
    return [
      {
        name: t('navigation.reporting.groups.overview'),
        items: [
          // { name: t('navigation.reporting.portfolioOverview'), path: ReportingPaths.PortfolioOverview, hasAccess: portfolio },
          { name: t('navigation.reporting.portfolioOverview2'), path: ReportingPaths.PortfolioOverview2, hasAccess: portfolio, isNew: true },
          {
            name: t('navigation.reporting.diversificationGeography'),
            path: ReportingPaths.PortfolioDiversification,
            view: 'geography',
            hasAccess: portfolio,
          },
        ],
      },
      {
        name: t('navigation.reporting.groups.esg'),
        items: [
          { name: t('navigation.reporting.energyProfile'), path: ReportingPaths.EnergyProfile, hasAccess: portfolio, isNew: true },
          { name: t('navigation.reporting.consumptionEnergy'), path: ReportingPaths.ConsumptionEnergy, hasAccess: eos },
          { name: t('navigation.reporting.consumptionBySource'), path: ReportingPaths.ConsumptionBySource, hasAccess: eos },
          { name: t('navigation.reporting.economyEnergyConsumption'), path: ReportingPaths.EconomyEnergyConsumption, hasAccess: eos },
          { name: t('navigation.reporting.consumptionTemperature'), path: ReportingPaths.ConsumptionTemperature, hasAccess: eos },
          { name: t('navigation.reporting.consumptionBySensor'), path: ReportingPaths.ConsumptionBySensor, hasAccess: eos },
          { name: t('navigation.reporting.ventilationVsDemand'), path: ReportingPaths.VentilationVsDemand, hasAccess: eos },
        ],
      },
      {
        name: t('navigation.reporting.groups.rentalContract'),
        items: [
          { name: t('navigation.reporting.rentalIncome'), path: ReportingPaths.RentalIncome, hasAccess: management },
          { name: t('navigation.reporting.occupancyRate'), path: ReportingPaths.OccupancyRate, hasAccess: management },
          { name: t('navigation.reporting.contractLength'), path: ReportingPaths.ContractLength, hasAccess: management },
          { name: t('navigation.reporting.expireContract'), path: ReportingPaths.ExpireContract, hasAccess: management },
          { name: t('navigation.reporting.vatPercentage'), path: ReportingPaths.VatPercentage, hasAccess: management },
          {
            name: t('navigation.reporting.diversificationSegment'),
            path: ReportingPaths.PortfolioDiversification,
            view: 'segment',
            hasAccess: portfolio,
          },
        ],
      },
      {
        name: t('navigation.reporting.groups.economy'),
        items: [
          { name: t('navigation.reporting.invoices'), path: ReportingPaths.Invoices, hasAccess: economy },
          { name: t('navigation.reporting.overdueInvoices'), path: ReportingPaths.OverdueInvoices, hasAccess: economy },
          { name: t('navigation.reporting.vacancyCost'), path: ReportingPaths.VacancyCost, hasAccess: economy },
          { name: t('navigation.reporting.financialOverview'), path: ReportingPaths.FinancialOverview, hasAccess: portfolio, isNew: true },
          { name: t('navigation.reporting.financing'), path: ReportingPaths.Financing, hasAccess: portfolio, isNew: true },
        ],
      },
      {
        name: t('navigation.reporting.groups.marketValue'),
        items: [
          { name: t('navigation.reporting.netYieldByBuilding'), path: ReportingPaths.NetYieldByBuilding, hasAccess: economy },
        ],
      },
    ];
  }, [t, getAvailablePackages]);

  // An item is active when its path matches; the two diversification entries share a path and are
  // disambiguated by the `?view=` param.
  const isItemActive = useCallback(
    (item: ReportItem) => pathname === item.path && (item.view ? currentView === item.view : true),
    [pathname, currentView],
  );

  const activeGroupName = useMemo(
    () => groups.find((group) => group.items.some(isItemActive))?.name,
    [groups, isItemActive],
  );

  const [openGroups, setOpenGroups] = useState<Set<string>>(() => new Set(activeGroupName ? [activeGroupName] : []));

  // Keep the group containing the active report expanded when navigating (e.g. via a direct URL).
  useEffect(() => {
    if (!activeGroupName) return;
    setOpenGroups((prev) => (prev.has(activeGroupName) ? prev : new Set(prev).add(activeGroupName)));
  }, [activeGroupName]);

  const toggleGroup = (name: string) =>
    setOpenGroups((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });

  // Preserve the current query string (e.g. date range) and set/clear the diversification view.
  const buildTo = (item: ReportItem) => {
    const params = new URLSearchParams(search);
    if (item.view) params.set('view', item.view);
    else params.delete('view');
    const qs = params.toString();
    return qs ? `${item.path}?${qs}` : item.path;
  };

  return (
    <motion.div
      className="flex flex-col gap-5 overflow-hidden"
      initial={{ x: 0 }}
      animate={{ x: isOpen ? 0 : -320 }}
      transition={{ duration: 0.3, ease: 'easeInOut' }}
    >
      <PageNavigation>
        <div className="relative">
          <PageBreadcrumbs>
            <PageBreadcrumbs.Link onClick={() => null}>{t('navigation.overview')}</PageBreadcrumbs.Link>
            <PageBreadcrumbs.Link>{t('navigation.navbar.reporting')}</PageBreadcrumbs.Link>
          </PageBreadcrumbs>
          <button
            onClick={() => setIsOpen(false)}
            className="absolute right-0 top-1/2 -translate-y-1/2 rounded-full hover:bg-brand-primary-hover transition-colors hidden lg:block"
          >
            <FaXmark className="text-white w-4 h-4" />
          </button>
        </div>
      </PageNavigation>
      <nav className="flex flex-1 flex-col" aria-label="Sidebar">
        <div className="space-y-2">
          {groups.map((group) => {
            const isGroupOpen = openGroups.has(group.name);
            return (
              <div key={group.name}>
                <button
                  type="button"
                  onClick={() => toggleGroup(group.name)}
                  aria-expanded={isGroupOpen}
                  className="flex w-full items-center gap-2 border-b border-border py-2 pl-1 pr-2 text-xs font-semibold uppercase tracking-wider text-white/50 hover:text-white/80 transition-colors"
                >
                  <FaChevronDown
                    className={classNames('w-3 h-3 flex-shrink-0 transition-transform duration-200', isGroupOpen ? '' : '-rotate-90')}
                  />
                  <span className="flex-1 text-left">{group.name}</span>
                </button>
                <motion.ul
                  initial={false}
                  animate={{ height: isGroupOpen ? 'auto' : 0, opacity: isGroupOpen ? 1 : 0 }}
                  transition={{ duration: 0.2, ease: 'easeInOut' }}
                  className="overflow-hidden"
                >
                  {group.items.map((item) => {
                    const active = isItemActive(item);
                    return (
                      <li key={item.name}>
                        <NavLink
                          to={buildTo(item)}
                          className={classNames(
                            'group flex items-center justify-between gap-x-3 border-l-2 border-b border-border/50 py-2 pl-3 pr-2 text-sm leading-6 font-medium transition ease-in-out duration-150',
                            active && 'border-l-brand-primary bg-surface-elevated text-brand-primary',
                            !active && 'border-l-transparent hover:bg-surface-hover',
                            !active && (item.hasAccess ? 'text-white' : 'text-white/40'),
                          )}
                        >
                          <span className="flex items-center gap-2">
                            {item.isNew ? (
                              <span className="text-amber-400 text-xs font-bold uppercase">
                                {t('navigation.reporting.newBadge')}
                              </span>
                            ) : null}
                            {item.name}
                          </span>
                          {item.hasAccess ? (
                            <FaArrowRight
                              className={classNames(
                                'w-3 h-3 flex-shrink-0 transition-colors',
                                active ? 'text-brand-primary' : 'text-white/30 group-hover:text-white/60',
                              )}
                            />
                          ) : (
                            <LockIcon />
                          )}
                        </NavLink>
                      </li>
                    );
                  })}
                </motion.ul>
              </div>
            );
          })}
        </div>
      </nav>
    </motion.div>
  );
}
